const FORM_KEYS = [
  'slug',
  'publishedAt',
  'linkedinUrl',
  'title_en',
  'title_tc',
  'title_sc',
  'summary_en',
  'summary_tc',
  'summary_sc',
  'body_en',
  'body_tc',
  'body_sc'
];

const BODY_ALIASES = {
  body_en: 'body_en',
  en: 'body_en',
  english: 'body_en',
  body_tc: 'body_tc',
  tc: 'body_tc',
  'traditional-chinese': 'body_tc',
  body_sc: 'body_sc',
  sc: 'body_sc',
  'simplified-chinese': 'body_sc'
};

export class PostMarkdownParseError extends Error {
  constructor(code, detail = '') {
    super(detail || code);
    this.name = 'PostMarkdownParseError';
    this.code = code;
  }
}

function unquote(value) {
  const v = String(value ?? '').trim();
  if (
    (v.startsWith('"') && v.endsWith('"') && v.length >= 2) ||
    (v.startsWith("'") && v.endsWith("'") && v.length >= 2)
  ) {
    return v.slice(1, -1).replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }
  return v;
}

function parseSimpleYaml(block) {
  const lines = String(block || '').replace(/\r\n/g, '\n').split('\n');
  const out = {};
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    if (!raw.trim() || raw.trim().startsWith('#')) {
      i += 1;
      continue;
    }
    const match = raw.match(/^([A-Za-z0-9_]+):\s*(.*)$/);
    if (!match) {
      i += 1;
      continue;
    }
    const key = match[1];
    const rest = match[2];
    if (rest === '|' || rest === '>' || rest === '|-') {
      const collected = [];
      i += 1;
      while (i < lines.length) {
        const next = lines[i];
        if (next.trim() === '') {
          collected.push('');
          i += 1;
          continue;
        }
        if (/^[A-Za-z0-9_]+:\s*/.test(next) && !/^\s/.test(next)) break;
        collected.push(next.replace(/^\s{2,}/, ''));
        i += 1;
      }
      out[key] = collected.join('\n').replace(/\n+$/, '');
      continue;
    }
    out[key] = unquote(rest);
    i += 1;
  }
  return out;
}

function parseBodySections(markdown) {
  const text = String(markdown || '').replace(/\r\n/g, '\n').trim();
  const bodies = { body_en: '', body_tc: '', body_sc: '' };
  if (!text) return bodies;

  const heading = /^##\s+([^\n]+)\s*$/gm;
  const matches = [...text.matchAll(heading)];
  if (!matches.length) {
    bodies.body_en = text;
    return bodies;
  }

  matches.forEach((match, index) => {
    const alias = String(match[1] || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
    const key = BODY_ALIASES[alias];
    if (!key) return;
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
    bodies[key] = text.slice(start, end).replace(/^\n+/, '').replace(/\n+$/, '');
  });

  return bodies;
}

function stripOuterFence(text) {
  const trimmed = String(text || '').replace(/^\uFEFF/, '').trim();
  const fenced = trimmed.match(/^```(?:markdown|md)?\s*\n([\s\S]*?)\n```$/i);
  return fenced ? fenced[1].trim() : trimmed;
}

function toDateInput(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return iso ? iso[1] : '';
}

function slugFromTitle(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function parsePostMarkdown(raw) {
  const text = stripOuterFence(raw);
  if (!text) {
    throw new PostMarkdownParseError('emptyFile');
  }

  let frontmatter = {};
  let rest = text;
  if (text.startsWith('---')) {
    const match = text.match(/^---[ \t]*\n([\s\S]*?)\n---[ \t]*\n?([\s\S]*)$/);
    if (!match) {
      throw new PostMarkdownParseError('missingFrontmatter');
    }
    frontmatter = parseSimpleYaml(match[1]);
    rest = match[2] || '';
  } else {
    throw new PostMarkdownParseError('missingFrontmatter');
  }

  const bodies = parseBodySections(rest);
  const titleEn = String(frontmatter.title_en || '').trim();
  const titleTc = String(frontmatter.title_tc || '').trim();
  const titleSc = String(frontmatter.title_sc || '').trim();
  if (!titleEn && !titleTc && !titleSc) {
    throw new PostMarkdownParseError('noTitle');
  }

  return {
    slug: String(frontmatter.slug || slugFromTitle(titleEn || titleTc || titleSc)).trim(),
    publishedAt: toDateInput(frontmatter.publishedAt),
    linkedinUrl: String(frontmatter.linkedinUrl || '').trim(),
    title_en: titleEn,
    title_tc: titleTc,
    title_sc: titleSc,
    summary_en: String(frontmatter.summary_en || '').trim(),
    summary_tc: String(frontmatter.summary_tc || '').trim(),
    summary_sc: String(frontmatter.summary_sc || '').trim(),
    body_en: bodies.body_en,
    body_tc: bodies.body_tc,
    body_sc: bodies.body_sc
  };
}

export function applyParsedMarkdownToForm(prev, parsed) {
  const next = { ...prev };
  FORM_KEYS.forEach((key) => {
    if (parsed[key] != null) next[key] = parsed[key];
  });
  return next;
}

export const HIRING_MARKDOWN_EXAMPLE = `---
slug: founders-analyst-ux
publishedAt: 2026-09-11
linkedinUrl:
title_en: Founder's Analyst Programme - UI/UX Designer
title_tc: 創辦人分析師計劃 - UI/UX 設計師
title_sc: 创办人分析师计划 - UI/UX 设计师
summary_en: As a UI/UX Designer, you will design how students interact with EDVise.
summary_tc: 作為 UI/UX 設計師，你將設計學生與 EDVise 的互動體驗。
summary_sc: 作为 UI/UX 设计师，你将设计学生与 EDVise 的互动体验。
---

## body_en

**EDVise** helps Hong Kong students plan university with data and AI.

### What you will do

- Design wireframes, prototypes, and high-fidelity mockups
- Map student journeys for JUPAS and elective choices
- Work with product and engineering to ship usable interfaces

### Our preferences

- A portfolio of web or app design work
- Strong Chinese (Cantonese or Mandarin); English is a plus

## body_tc

**惟思教育科技**以數據和 AI 幫助香港學生規劃升學。

### 你將負責

- 設計線框、原型和高保真介面
- 為聯招和選科規劃學生旅程
- 與產品及工程團隊合作推出易用介面

### 我們的偏好

- 附上網站或應用設計作品集
- 精通中文（粵語或普通話）；英語為佳

## body_sc

**惟思教育科技**以数据和 AI 帮助香港学生规划升学。

### 你将负责

- 设计线框、原型和高保真界面
- 为联招和选科规划学生旅程
- 与产品及工程团队合作推出易用界面

### 我们的偏好

- 附上网站或应用设计作品集
- 精通中文（粤语或普通话）；英语为佳
`;

export const PRESS_MARKDOWN_EXAMPLE = `---
slug: 20260911-product-update
publishedAt: 2026-09-11
linkedinUrl:
title_en: EDVise launches a new planning update
title_tc: 惟思教育科技推出新規劃更新
title_sc: 惟思教育科技推出新规划更新
summary_en: A short listing summary of the announcement.
summary_tc: 公告的簡短列表摘要。
summary_sc: 公告的简短列表摘要。
---

## body_en

Share the news in **clear markdown**.

- Use short paragraphs
- Use lists for facts and dates

## body_tc

用**清晰的 Markdown** 寫出公告。

- 用短段落
- 用列表寫事實和日期

## body_sc

用**清晰的 Markdown** 写出公告。

- 用短段落
- 用列表写事实和日期
`;

export const HIRING_AI_PROMPT = `You are writing a careers opening for EDVise (惟思教育科技), a Hong Kong education-tech company.

Return ONE markdown file only. No extra commentary. Do not wrap the file in a code fence.

Use this exact shape:

---
slug: kebab-case-id
publishedAt: YYYY-MM-DD
linkedinUrl:
title_en:
title_tc:
title_sc:
summary_en:
summary_tc:
summary_sc:
---

## body_en

...

## body_tc

...

## body_sc

...

Rules:
- English title, summary, and body are required. Traditional Chinese (Hong Kong) and Simplified Chinese are optional; leave those keys/sections empty if you do not have them.
- Career titles must use this programme name:
  - EN: Founder's Analyst Programme - {Role}
  - TC: 創辦人分析師計劃 - {職位}
  - SC: 创办人分析师计划 - {职位}
- slug: lowercase letters, numbers, and hyphens only. No spaces. Unique. Example: founders-analyst-ux
- publishedAt: ISO date YYYY-MM-DD
- linkedinUrl: optional. Leave blank if unknown. Do not invent URLs.
- summary_*: one or two sentences for the listing card. No headings.
- body_*: the full posting. Use GitHub-flavored markdown that EDVise already renders:
  - **bold**, *italic*, ~~strikethrough~~
  - headings (##, ###)
  - bullet and numbered lists
  - short paragraphs
- Do not include images, HTML, or tables.
- Do not mention JUPART. Do not use 聯招分析師計劃 as the programme name.

Write from the notes I paste next. If I only give English, fill English and leave TC/SC empty.
`;

export const PRESS_AI_PROMPT = `You are writing a press release for EDVise (惟思教育科技), a Hong Kong education-tech company.

Return ONE markdown file only. No extra commentary. Do not wrap the file in a code fence.

Use this exact shape:

---
slug: kebab-case-id
publishedAt: YYYY-MM-DD
linkedinUrl:
title_en:
title_tc:
title_sc:
summary_en:
summary_tc:
summary_sc:
---

## body_en

...

## body_tc

...

## body_sc

...

Rules:
- English title, summary, and body are required. Traditional Chinese (Hong Kong) and Simplified Chinese are optional; leave those keys/sections empty if you do not have them.
- slug: lowercase letters, numbers, and hyphens only. No spaces. Prefer YYYYMMDD-topic, e.g. 20260911-product-update
- publishedAt: ISO date YYYY-MM-DD
- linkedinUrl: optional. Leave blank if unknown. Do not invent URLs.
- summary_*: one or two sentences for the listing card. No headings.
- body_*: the full announcement. Use GitHub-flavored markdown that EDVise already renders:
  - **bold**, *italic*, ~~strikethrough~~
  - headings (##, ###)
  - bullet and numbered lists
  - short paragraphs
- Do not include images, HTML, or tables.

Write from the notes I paste next. If I only give English, fill English and leave TC/SC empty.
`;
